/**
 * Focus store — global reactive set of "In Focus" task IDs.
 *
 * Provides instant access to the focused task ID set without per-component
 * Dexie queries. Populated by FocusBanner's live query + localStorage cache
 * for instant startup rendering.
 *
 * The cache also enables the FocusBanner to render immediately on app start
 * (before Dexie queries resolve), matching the placeholder system behavior.
 */

import { create } from "zustand"
import type { DbObject } from "@totodo/operations"

// ---------------------------------------------------------------------------
// localStorage cache key
// ---------------------------------------------------------------------------

const LS_KEY = "totodo:focus-cache"

/**
 * Cached fields — just enough to render the banner instantly.
 * Full DbObject fields arrive when the live query resolves.
 */
interface CachedFocusTask {
  id: string
  title: string
  type: number
  parentPath: string
  status: number
  tags?: string[]
  icon?: string
  dueAt?: string
  userStatus?: string
  docHtml?: string
}

interface FocusStoreState {
  /** Set of focused task IDs (for quick membership checks) */
  focusedIds: Set<string>

  /** Cached task data for instant banner rendering on startup */
  cachedTasks: CachedFocusTask[]

  /** Whether live data has arrived (cache only until then) */
  isLive: boolean

  /** Update from live query data */
  setFromLiveData: (tasks: DbObject[]) => void

  /** Check if a task is focused (O(1)) */
  isFocused: (taskId: string) => boolean
}

// ---------------------------------------------------------------------------
// Read initial cache
// ---------------------------------------------------------------------------

function readCache(): { ids: Set<string>; tasks: CachedFocusTask[] } {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return { ids: new Set(), tasks: [] }
    const tasks = JSON.parse(raw) as CachedFocusTask[]
    return { ids: new Set(tasks.map((t) => t.id)), tasks }
  } catch {
    return { ids: new Set(), tasks: [] }
  }
}

function writeCache(tasks: DbObject[]): void {
  try {
    const slim: CachedFocusTask[] = tasks.map((t) => ({
      id: t.id!,
      title: t.title,
      type: t.type,
      parentPath: t.parentPath,
      status: t.status,
      tags: t.tags?.length ? t.tags : undefined,
      icon: t.icon || undefined,
      dueAt: t.dueAt || undefined,
      userStatus: t.userStatus ? String(t.userStatus) : undefined,
      docHtml: t.docHtml ? "1" : undefined,
    }))
    if (slim.length === 0) {
      localStorage.removeItem(LS_KEY)
    } else {
      localStorage.setItem(LS_KEY, JSON.stringify(slim))
    }
  } catch {
    // localStorage unavailable
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const initial = readCache()

export const useFocusStore = create<FocusStoreState>((set, get) => ({
  focusedIds: initial.ids,
  cachedTasks: initial.tasks,
  isLive: false,

  setFromLiveData: (tasks: DbObject[]) => {
    const ids = new Set(tasks.map((t) => t.id!).filter(Boolean))
    set({ focusedIds: ids, isLive: true, cachedTasks: [] })
    writeCache(tasks)
  },

  isFocused: (taskId: string) => get().focusedIds.has(taskId),
}))

/**
 * Non-hook accessor — check if a task is in focus from outside React.
 */
export function isTaskInFocusSync(taskId: string): boolean {
  return useFocusStore.getState().focusedIds.has(taskId)
}
